import { useNavigate } from 'react-router-dom'
import {
  Button,
  Stack,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material'
import { ArrowBack, CameraAlt, GridOn } from '@mui/icons-material'

import { useAssignedDigitalTrays } from '../AssignedDigitalTrays/AssignedDigitalTrays.context'
import './TrayDetails.scss'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { useIsMobile } from 'lib/utils/mediaQueries'
import { getLatestTrayImage } from 'lib/utils/getLatestTrayImage'
import useDTMS3Image from 'lib/utils/useDTMS3Image'

const TrayDetails = () => {
  const navigate = useNavigate()
  const { selectedSubTray, selectedTray, setUsageScrews, setUsagePlates } =
    useAssignedDigitalTrays()
  const isMobile = useIsMobile()
  const subTrayDetails = extractSubTrayJSONData(selectedSubTray)
  const manufacturer =
    selectedSubTray?.companyName ||
    selectedTray?.companyName ||
    subTrayDetails?.trayType
  const trayImage = getLatestTrayImage(subTrayDetails, ['nurse'])
  const isInstrumentOnly = subTrayDetails?.trayType === 'instrument'

  const { imageUrl, isLoading, error } = useDTMS3Image({ url: trayImage })

  if (!selectedSubTray || !selectedTray) {
    return (
      <div className="tray-details-error">
        <Typography variant="h6" color="error">
          No tray selected
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          Return to Trays
        </Button>
      </div>
    )
  }

  return (
    <div className="tray-details-container">
      <div className="tray-details-header">
        <Button
          className="back-button"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Back' : 'Back to Trays'}
        </Button>

        {!isMobile && (
          <Breadcrumbs className="breadcrumbs" aria-label="breadcrumb">
            <Link
              component="button"
              variant="body1"
              onClick={() => navigate(-1)}
              color="inherit"
            >
              Assigned Trays
            </Link>
            <Typography color="text.primary">
              {selectedSubTray.description || selectedTray.description}
            </Typography>
          </Breadcrumbs>
        )}
      </div>

      <Paper elevation={0} className="tray-details-content">
        <Typography variant={isMobile ? 'h5' : 'h4'} className="tray-title">
          {selectedSubTray.description}
        </Typography>

        <div className="tray-metadata">
          <Typography variant="body1" color="text.secondary">
            Tray Type: {subTrayDetails?.trayType || 'Unknown'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Vendor: {manufacturer}
          </Typography>
        </div>

        <div className="tray-image-section">
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            className="section-title"
          >
            Tray Image
          </Typography>
          <Paper variant="outlined" className="tray-image-container">
            {imageUrl ? (
              <img src={imageUrl} alt="Tray" className="tray-image" />
            ) : (
              <div className="no-image-placeholder">
                <Typography color="text.secondary">
                  {isLoading
                    ? 'Loading...'
                    : error || 'No tray image available'}
                </Typography>
              </div>
            )}
          </Paper>
        </div>

        {!isInstrumentOnly && (
          <div className="action-section">
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              className="section-title"
            >
              Document Using:
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              className="action-buttons"
            >
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GridOn />}
                onClick={() => {
                  setUsageScrews([])
                  setUsagePlates([])
                  navigate(`../../tray-visualization`)
                }}
                size={isMobile ? 'medium' : 'large'}
              >
                Smart Tray
              </Button>
              <Button
                variant="contained"
                fullWidth
                startIcon={<CameraAlt />}
                onClick={() => {
                  setUsageScrews([])
                  setUsagePlates([])
                  navigate(`../../capture-tray/${selectedTray.id}?mode=analyze`)
                }}
                size={isMobile ? 'medium' : 'large'}
              >
                Smart Vision
              </Button>
            </Stack>
          </div>
        )}
      </Paper>
    </div>
  )
}

export default TrayDetails
